"use client";

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchPayments, fetchRefunds } from "../../../services/billingService";
import { extractApiErrorMessage } from "../../../utils/error-handler";
import { formatPaise, formatBillingDate } from "../../../utils/billingFormat";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import RefundPaymentModal from "./RefundPaymentModal";

const REFUNDABLE_STATUSES = ["SUCCESS", "PARTIALLY_REFUNDED"];

const SchoolPaymentsHistory = () => {
  const { schoolId } = useParams();
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refundingPayment, setRefundingPayment] = useState(null);

  const load = () => {
    return Promise.all([fetchPayments(schoolId), fetchRefunds(schoolId)])
      .then(([paymentsRes, refundsRes]) => {
        setPayments(paymentsRes.data || []);
        setRefunds(refundsRes.data || []);
      })
      .catch((err) => setError(extractApiErrorMessage(err)));
  };

  useEffect(() => {
    load().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId]);

  const refundedAmountFor = (paymentId) =>
    refunds.filter((r) => r.payment === paymentId).reduce((sum, r) => sum + r.amount, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-900">Payments</h1>
        <Button variant="outline" onClick={() => navigate(`/dashboard/schools/${schoolId}/billing`)}>
          Back to Billing
        </Button>
      </div>

      {error && <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700 text-sm">{error}</div>}

      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-x-auto">
        {loading ? (
          <div className="py-16 text-center text-gray-400 text-sm">Loading...</div>
        ) : payments.length === 0 ? (
          <div className="py-16 text-center text-gray-400 text-sm">No payments recorded yet.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Amount</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Purpose</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Razorpay Order / Payment</TableHead>
                <TableHead>Paid At</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.map((p) => {
                const refundedAmount = refundedAmountFor(p._id);
                const remaining = p.totalAmount - refundedAmount;
                return (
                  <TableRow key={p._id}>
                    <TableCell>{formatPaise(p.totalAmount)}</TableCell>
                    <TableCell>{p.provider}</TableCell>
                    <TableCell>{p.paymentMethod || "-"}</TableCell>
                    <TableCell>{p.purpose}</TableCell>
                    <TableCell>
                      <Badge variant={p.status === "SUCCESS" ? "default" : "secondary"}>{p.status}</Badge>
                      {refundedAmount > 0 && (
                        <div className="text-xs text-gray-500 mt-1">Refunded: {formatPaise(refundedAmount)}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      {p.provider === "RAZORPAY" ? (
                        <div className="text-xs text-gray-500 space-y-0.5">
                          <div>Order: {p.razorpayOrderId || "-"}</div>
                          <div>Payment: {p.razorpayPaymentId || "-"}</div>
                        </div>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>{formatBillingDate(p.paidAt)}</TableCell>
                    <TableCell className="text-right">
                      {REFUNDABLE_STATUSES.includes(p.status) && remaining > 0 && (
                        <Button variant="outline" size="sm" onClick={() => setRefundingPayment({ payment: p, remaining })}>
                          Refund
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <RefundPaymentModal
        isOpen={Boolean(refundingPayment)}
        onClose={() => setRefundingPayment(null)}
        onSaved={load}
        schoolId={schoolId}
        payment={refundingPayment?.payment}
        remainingRefundable={refundingPayment?.remaining}
      />
    </div>
  );
};

export default SchoolPaymentsHistory;
